'use client';

import { usePathname } from '@/i18n/navigation';
import Lenis from 'lenis';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

interface SmoothScrollerContextProps {
    lenis: Lenis | null;
    // eslint-disable-next-line no-unused-vars
    setOrientation: (orientation: 'vertical' | 'horizontal') => void;
}

const SmoothScrollerContext = createContext<SmoothScrollerContextProps | null>(null);

export const useSmoothScroller = () => useContext(SmoothScrollerContext);

const SmoothScrollContext = ({ children }: { children: React.ReactNode }) => {
    const [lenis, setLenis] = useState<Lenis | null>(null);
    const pathname = usePathname();

    const setOrientation = useCallback(
        (orientation: 'vertical' | 'horizontal') => {
            if (lenis) {
                lenis.options.orientation = orientation;
                lenis.options.gestureOrientation = 'both';
            }
        },
        [lenis],
    );

    useEffect(() => {
        if (lenis) {
            if (typeof window !== 'undefined') {
                const urlAnchor = window.location.hash;
                const anchorWithoutTrailingSlash = urlAnchor.replace(/\/$/, '');

                if (anchorWithoutTrailingSlash) {
                    lenis.scrollTo(anchorWithoutTrailingSlash);
                    return;
                }
            }
            lenis.scrollTo(0, { immediate: true });
        }
    }, [pathname, lenis]);

    useEffect(() => {
        const scroller = new Lenis({
            duration: 3,
            easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        });

        const raf = (time: number) => {
            scroller.raf(time);
            requestAnimationFrame(raf);
        };

        requestAnimationFrame(raf);
        setLenis(scroller);

        return () => {
            scroller.destroy();
        };
    }, []);

    return (
        <SmoothScrollerContext.Provider value={{ lenis, setOrientation }}>
            {children}
        </SmoothScrollerContext.Provider>
    );
};

export default SmoothScrollContext;
